import { afterEveryRender, Component, ElementRef, HostBinding, inject, Input, viewChild } from '@angular/core';
import { SolButtonComponent } from '../sol-button/sol-button.component';
import { SolMarkdownComponent } from '../sol-markdown/sol-markdown.component';
import { AbstractComponent } from '../../abstract.component';
import { LocaleService } from '../../../services/locale.service';
import { LocaleTools } from '../../../tools/locale-tools';
import { SolTextBox } from '../../../models/core/sol-text-box';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolTextBoxState } from './sol-text-box.state';

@Component({
  selector: 'sol-text-box',
  imports: [SolButtonComponent, SolMarkdownComponent],
  templateUrl: './sol-text-box.component.html',
})
export class SolTextBoxComponent extends AbstractComponent<SolTextBoxState> {
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly localeService = inject(LocaleService);

  // View
  private readonly content = viewChild<ElementRef<HTMLElement>>('content');

  // Computed
  protected readonly visibleText$ = this.compute(state => state.visibleText);
  protected readonly showMoreVisible$ = this.compute(state => state.showMoreVisible);
  protected readonly showLessVisible$ = this.compute(state => state.showLessVisible);
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly markdown$ = this.compute(state => state.box.markdown);
  protected readonly markdownFeatures$ = this.compute(state => state.box.markdownFeatures);
  protected readonly linkTarget$ = this.compute(state => state.box.linkTarget);

  constructor() {
    super(SolTextBoxState.of());

    // Watch translations
    this.localeService.watchComputed(
      lss => lss.translations,
      () => this.updateTranslatedText(),
    );

    // Watch box changes
    this.watchComputed(
      state => state.box,
      () => this.updateTranslatedText(),
    );

    afterEveryRender(() => this.measureOverflow());
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  @Input()
  public set box(box: SolTextBox) {
    this.update(state => state.withBox(box));
  }

  protected onToggle(): void {
    this.update(state => state.withExpanded(!state.expanded));
  }

  private measureOverflow(): void {
    const element = this.content()?.nativeElement;
    if (element === undefined || !this.state.clamped) {
      return;
    }
    const overflow = element.scrollHeight - element.clientHeight > 1;
    this.update(state => state.withOverflow(overflow));
  }

  private updateTranslatedText(): void {
    let textTranslated: string;
    const box = this.state.box;
    if (box.text !== undefined) {
      textTranslated = this.localeService.translateText(box.text);
    } else {
      // Find correct translation
      const lss = this.localeService.state;
      const languageId = LocaleTools.toCurrentLanguageId(lss);
      textTranslated = LocaleTools.toCorrectTranslation(box.translations, languageId);
    }
    this.update(state => state.withTranslatedText(textTranslated));
  }
}
